import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from './Common/Loader';
import { IUser, useSinginMutation } from '../generated/graphql';
import { AppContext } from '../AppContext';
import { StringOrNull } from '../utils/types';
import { AuthRoutes } from '../enums/RouteEnums';
import { AppConsts } from '../enums/AppConsts';
import { Actions } from '../enums/ActionEnums';
import { Box, Button, Grid, TextField, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { buttons, Colors } from '../utils/theme';
import logo from '../assets/images/logos/logo_blue.svg';
import InputComponent from './Common/Fields/InputComponent';

interface errorsType {
  username: StringOrNull;
  password: StringOrNull;
  login: StringOrNull;
}

const errors: errorsType = {
  username: null,
  password: null,
  login: null
};

const errorTexts = {
  username: 'Username is required!',
  password: 'Password is required!',
  login: 'Invalid username or password'
};

const Login: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [singin, signinResult] = useSinginMutation();
  const [formErrors, setFormErrors] = useState(errors);
  const { state, dispatch } = useContext(AppContext);

  useEffect(() => {
    if (state.user.data) {
      navigate(AuthRoutes.Home);
    }
  }, []);

  const login = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = username.length > 0 && password.length > 0;
    setFormErrors({
      ...formErrors,
      username: username.length === 0 ? errorTexts.username : null,
      password: password.length === 0 ? errorTexts.password : null
    });
    if (isValid) {
      singin({
        variables: { input: { username, password } },
        update(_, { data }) {
          if (data) {
            const { token, user } = data.result;
            localStorage.setItem(AppConsts.AccessToken, token);
            dispatch({ type: Actions.SetUser, payload: { data: user as IUser, token } });
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            navigate(location.state?.path ?? AuthRoutes.Home);
          }
        }
      }).catch(() =>
        setFormErrors({
          ...formErrors,
          login: 'Invalid username or password'
        })
      );
    }
  };

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setFormErrors({
      ...formErrors,
      login: null,
      username: value.length > 0 ? null : errorTexts.username
    });
    setUsername(value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setFormErrors({
      ...formErrors,
      login: null,
      password: value.length > 0 ? null : errorTexts.password
    });
    setPassword(value);
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        background:
          'transparent linear-gradient(270deg, #2274BC 0%, #12497F 100%) 0% 0% no-repeat padding-box',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Box
        component="form"
        noValidate
        onSubmit={login}
        sx={{ padding: '100px 88px', background: Colors.white, borderRadius: '12px' }}
      >
        <Box
          sx={{
            width: '265px',
            display: 'flex',
            flexFlow: 'column',
            gap: '70px',
            alignItems: 'center'
          }}
        >
          <Box component="img" src={logo} alt="logo" sx={{ width: '71px', height: '81px' }} />
          <Box
            sx={{
              display: 'flex',
              gap: '30px',
              flexFlow: 'column',
              width: '100%',
              position: 'relative',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {formErrors.login && (
              <Typography
                data-testid="error-input-field"
                color="error"
                sx={{ position: 'absolute', top: '-45px' }}
              >
                {formErrors.login}
              </Typography>
            )}
            <InputComponent
              id="username"
              label="Username"
              value={username}
              onChange={handleUsernameChange}
              errorMsg={formErrors.username}
            />
            <InputComponent
              id="password"
              label="Password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              errorMsg={formErrors.password}
            />
          </Box>
          <Button
            data-testid="login-button"
            variant="contained"
            color="primary"
            aria-label="LOG IN"
            type="submit"
            fullWidth
            sx={{ ...buttons.loginButton, width: '100%', margin: 0 }}
          >
            LOGIN
          </Button>
        </Box>
      </Box>
      <Loader loading={signinResult.loading} />
    </Box>
  );
};

export default Login;
